import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ApiService from '../services/apiService';
import { ExportConfiguration, ExportPushResult, PraktijkdataSearchResult, SummaryRef } from '../types';
import { logger } from '../utils/logger';

interface PraktijkdataExportFlowProps {
    fileId: string;
    summaryRef: SummaryRef;
    /** Alle PRAKTIJKDATA-configuraties voor dit template */
    configs: ExportConfiguration[];
    summaryTitle: string;
    /** ISO-datum van het verslag/consult; default voor het zoekbereik */
    defaultDate?: string;
    /** Terug naar het gewone deelmenu */
    onBack: () => void;
    /** Hele deel-dialoog sluiten */
    onClose: () => void;
}

// zoeken → afspraak kiezen (in de resultatenlijst) → bevestigen → link-call →
// resultaat. Alle stappen leven in dezelfde dialoog.
type FlowStep = 'search' | 'confirm' | 'linking' | 'success' | 'error';

const MAX_RANGE_DAYS = 100;

// Datum als YYYY-MM-DD zonder tijdzone-verrassingen (toISOString zou rond
// middernacht een dag kunnen verspringen).
const toDateInputValue = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const parseDateInput = (value: string): Date | null => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match) return null;
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return Number.isNaN(date.getTime()) ? null : date;
};

const daysBetween = (start: Date, end: Date): number =>
    Math.round((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000));

const addDays = (date: Date, days: number): Date => {
    const copy = new Date(date.getTime());
    copy.setDate(copy.getDate() + days);
    return copy;
};

const PraktijkdataExportFlow: React.FC<PraktijkdataExportFlowProps> = ({
    fileId,
    summaryRef,
    configs,
    summaryTitle,
    defaultDate,
    onBack,
    onClose,
}) => {
    const { t, i18n } = useTranslation();
    const apiService = useMemo(() => new ApiService(), []);

    // Basisdatum voor defaults en snelkoppelingen: verslagdatum, anders vandaag.
    const baseDate = useMemo(() => {
        if (defaultDate) {
            const parsed = parseDateInput(defaultDate.slice(0, 10));
            if (parsed) return parsed;
        }
        return new Date();
    }, [defaultDate]);

    const [step, setStep] = useState<FlowStep>('search');
    const [selectedConfigId, setSelectedConfigId] = useState<string>(configs[0]?.id ?? '');
    const [datumStart, setDatumStart] = useState<string>(toDateInputValue(baseDate));
    const [datumEind, setDatumEind] = useState<string>(toDateInputValue(baseDate));
    const [isSearching, setIsSearching] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [searchError, setSearchError] = useState<string | null>(null);
    // Cliëntnamen uit het EPD: uitsluitend in deze component-state houden;
    // niet persisten, niet loggen, niet doorgeven aan analytics.
    const [results, setResults] = useState<PraktijkdataSearchResult[]>([]);
    const [linkConfigId, setLinkConfigId] = useState<string>('');
    const [selectedResult, setSelectedResult] = useState<PraktijkdataSearchResult | null>(null);
    const [linkResult, setLinkResult] = useState<ExportPushResult | null>(null);

    // Client-side spiegel van de backend-validatie; voorkomt onnodige roundtrips.
    const validationError = useMemo((): string | null => {
        const start = parseDateInput(datumStart);
        const end = parseDateInput(datumEind);
        if (!start || !end) {
            return t('shareDialog.praktijkdata.validationRequired');
        }
        if (daysBetween(start, end) < 0) {
            return t('shareDialog.praktijkdata.validationOrder');
        }
        if (daysBetween(start, end) > MAX_RANGE_DAYS) {
            return t('shareDialog.praktijkdata.validationRange', { days: MAX_RANGE_DAYS });
        }
        return null;
    }, [datumStart, datumEind, t]);

    const applyShortcut = (daysAround: number) => {
        if (daysAround === 0) {
            const today = toDateInputValue(new Date());
            setDatumStart(today);
            setDatumEind(today);
        } else {
            setDatumStart(toDateInputValue(addDays(baseDate, -daysAround)));
            setDatumEind(toDateInputValue(addDays(baseDate, daysAround)));
        }
    };

    const search = async () => {
        if (validationError || isSearching) return;
        setIsSearching(true);
        setSearchError(null);
        try {
            const response = await apiService.searchPraktijkdata(
                fileId,
                summaryRef,
                selectedConfigId,
                datumStart,
                datumEind
            );
            setResults(response.results);
            setLinkConfigId(response.configId);
            setHasSearched(true);
        } catch (error: any) {
            // Alleen de foutstatus loggen; response-data kan cliëntgegevens bevatten.
            logger.error('Praktijkdata search failed with status:', error?.response?.status);
            const message = error?.response?.data?.error;
            setSearchError(message ? message.toString() : t('shareDialog.praktijkdata.error'));
        } finally {
            setIsSearching(false);
        }
    };

    const link = async () => {
        if (!selectedResult) return;
        setStep('linking');
        try {
            const result = await apiService.linkPraktijkdata(
                fileId,
                summaryRef,
                linkConfigId || selectedConfigId,
                selectedResult.id
            );
            setLinkResult(result);
            setStep(result.success ? 'success' : 'error');
        } catch (error: any) {
            logger.error('Praktijkdata link failed with status:', error?.response?.status);
            const message = error?.response?.data?.error;
            setLinkResult({
                success: false,
                message: message ? message.toString() : t('shareDialog.praktijkdata.error'),
            });
            setStep('error');
        }
    };

    // "2026-07-07T09:00:00" is lokale praktijktijd zonder offset: letterlijk
    // tonen, dus zelf splitsen en niet via de Date-tijdzonemachinerie halen.
    const formatDatumTijd = (value?: string): string => {
        if (!value) return '';
        const [datePart, timePart] = value.split('T');
        const parsed = parseDateInput(datePart);
        const dateLabel = parsed ? parsed.toLocaleDateString(i18n.language) : datePart;
        const timeLabel = timePart ? timePart.slice(0, 5) : '';
        return timeLabel ? `${dateLabel} ${timeLabel}` : dateLabel;
    };

    const dateInputClass =
        'w-full px-3 py-2 text-sm rounded-lg border border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500';
    const primaryButtonClass =
        'px-4 py-2 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
    const secondaryButtonClass =
        'px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors';

    if (step === 'search') {
        return (
            <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900">
                    {t('shareDialog.praktijkdata.searchTitle')}
                </h3>

                {configs.length > 1 && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('shareDialog.praktijkdata.configLabel')}
                        </label>
                        <select
                            value={selectedConfigId}
                            onChange={(e) => setSelectedConfigId(e.target.value)}
                            disabled={isSearching}
                            className={dateInputClass}
                        >
                            {configs.map((config) => (
                                <option key={config.id} value={config.id}>{config.name}</option>
                            ))}
                        </select>
                    </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('shareDialog.praktijkdata.dateFrom')}
                        </label>
                        <input
                            type="date"
                            value={datumStart}
                            onChange={(e) => setDatumStart(e.target.value)}
                            disabled={isSearching}
                            className={dateInputClass}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('shareDialog.praktijkdata.dateTo')}
                        </label>
                        <input
                            type="date"
                            value={datumEind}
                            onChange={(e) => setDatumEind(e.target.value)}
                            disabled={isSearching}
                            className={dateInputClass}
                        />
                    </div>
                </div>

                {/* Snelkoppelingen om het bereik rond de verslagdatum te verruimen */}
                <div className="flex flex-wrap gap-2">
                    {[
                        { label: t('shareDialog.praktijkdata.shortcutToday'), days: 0 },
                        { label: t('shareDialog.praktijkdata.shortcutWeek'), days: 7 },
                        { label: t('shareDialog.praktijkdata.shortcutMonth'), days: 30 },
                    ].map(({ label, days }) => (
                        <button
                            key={label}
                            onClick={() => applyShortcut(days)}
                            disabled={isSearching}
                            className="px-3 py-1 text-xs rounded-full border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors"
                        >
                            {label}
                        </button>
                    ))}
                </div>

                {validationError && (
                    <p className="text-xs text-red-600">{validationError}</p>
                )}

                <div className="flex justify-end space-x-2">
                    <button onClick={onBack} disabled={isSearching} className={secondaryButtonClass}>
                        {t('shareDialog.praktijkdata.back')}
                    </button>
                    <button
                        onClick={search}
                        disabled={!!validationError || isSearching}
                        className={primaryButtonClass}
                    >
                        {t('shareDialog.praktijkdata.search')}
                    </button>
                </div>

                {isSearching && (
                    <div className="flex justify-center py-4">
                        <div className="w-5 h-5 border-2 border-gray-300 border-t-primary-600 rounded-full animate-spin"></div>
                    </div>
                )}

                {searchError && !isSearching && (
                    <div className="text-center space-y-2">
                        <p className="text-sm text-red-600">{searchError}</p>
                        <button onClick={search} className={secondaryButtonClass}>
                            {t('shareDialog.praktijkdata.retry')}
                        </button>
                    </div>
                )}

                {hasSearched && !isSearching && !searchError && results.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-2">
                        {t('shareDialog.praktijkdata.empty')}
                    </p>
                )}

                {hasSearched && !isSearching && !searchError && results.length > 0 && (
                    <div>
                        <div className="text-sm font-medium text-gray-700 mb-2">
                            {t('shareDialog.praktijkdata.resultsLabel')}
                        </div>
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                            {results.map((result, index) => (
                                <button
                                    key={`${result.id}-${result.afspraakId ?? index}`}
                                    onClick={() => {
                                        setSelectedResult(result);
                                        setStep('confirm');
                                    }}
                                    className="w-full flex items-center p-3 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-colors text-left"
                                >
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium text-gray-900 text-sm truncate">{result.name}</div>
                                        <div className="text-gray-500 text-xs">
                                            {[formatDatumTijd(result.datumTijd), result.afspraaktype]
                                                .filter(Boolean)
                                                .join(' · ')}
                                        </div>
                                    </div>
                                    {result.status && (
                                        <span className="ml-3 flex-shrink-0 px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
                                            {result.status}
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    if (step === 'confirm' && selectedResult) {
        return (
            <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900">
                    {t('shareDialog.praktijkdata.searchTitle')}
                </h3>
                {/* Expliciete bevestiging: er wordt naar een medisch dossier geschreven. */}
                <p className="text-sm text-gray-900">
                    {t('shareDialog.praktijkdata.confirm', {
                        title: summaryTitle,
                        name: selectedResult.name,
                    })}
                </p>
                <div className="text-xs text-gray-500">
                    {[formatDatumTijd(selectedResult.datumTijd), selectedResult.afspraaktype]
                        .filter(Boolean)
                        .join(' · ')}
                </div>
                <div className="flex justify-end space-x-2">
                    <button onClick={() => setStep('search')} className={secondaryButtonClass}>
                        {t('shareDialog.praktijkdata.back')}
                    </button>
                    <button onClick={link} className={primaryButtonClass}>
                        {t('shareDialog.praktijkdata.confirmAction')}
                    </button>
                </div>
            </div>
        );
    }

    if (step === 'linking') {
        return (
            <div className="flex flex-col items-center py-8 text-center">
                <div className="w-8 h-8 border-2 border-gray-300 border-t-primary-600 rounded-full animate-spin mb-4"></div>
                <div className="text-sm font-medium text-gray-900">{t('shareDialog.praktijkdata.busy')}</div>
                <div className="text-xs text-gray-500 mt-1">{t('shareDialog.praktijkdata.busyHint')}</div>
            </div>
        );
    }

    if (step === 'success') {
        return (
            <div className="flex flex-col items-center py-6 text-center">
                <svg className="w-8 h-8 text-primary-600 mb-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-gray-900">
                    {linkResult?.message || t('shareDialog.praktijkdata.success')}
                </p>
                <button onClick={onClose} className={`${secondaryButtonClass} mt-4`}>
                    {t('shareDialog.praktijkdata.close')}
                </button>
            </div>
        );
    }

    // step === 'error' (of confirm zonder selectie, wat niet voor hoort te komen)
    return (
        <div className="flex flex-col items-center py-6 text-center">
            <svg className="w-8 h-8 text-red-600 mb-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <p className="text-sm text-gray-900">
                {linkResult?.message || t('shareDialog.praktijkdata.error')}
            </p>
            <div className="flex space-x-2 mt-4">
                {/* Geen automatische retries: opnieuw proberen is een bewuste actie. */}
                <button onClick={link} className={primaryButtonClass}>
                    {t('shareDialog.praktijkdata.retry')}
                </button>
                <button onClick={() => setStep('search')} className={secondaryButtonClass}>
                    {t('shareDialog.praktijkdata.back')}
                </button>
            </div>
        </div>
    );
};

export default PraktijkdataExportFlow;
